import { useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  clearUnsavedChanges,
  hasUnsavedChanges,
  isGuardedInAppNavigation,
  subscribeUnsavedChanges
} from '../../shared/lib/unsaved-changes';
import { Button } from '../../shared/ui/Button';
import { ModalDialog } from '../../shared/ui/ModalDialog';

/**
 * Назначение: предупреждение «Есть несохранённые изменения» при уходе со страницы с изменённой формой.
 * Кто: все сотрудники staff SPA.
 * Вход: смонтирован в AppShell; формы отмечают себя через `useUnsavedChangesGuard(dirty)`.
 * Перехватывает переходы по ссылкам внутри CRM (меню, кнопки-ссылки) и закрытие/перезагрузку вкладки.
 */
export function UnsavedChangesGuard() {
  const navigate = useNavigate();
  const dirty = useSyncExternalStore(subscribeUnsavedChanges, hasUnsavedChanges, () => false);
  const [pendingTo, setPendingTo] = useState<string | null>(null);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Старые браузеры показывают окно только при непустом returnValue.
      e.returnValue = '';
    };
    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target as Element | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || !isGuardedInAppNavigation(anchor, window.location)) return;
      e.preventDefault();
      e.stopPropagation();
      const url = new URL(anchor.href, window.location.href);
      setPendingTo(`${url.pathname}${url.search}${url.hash}`);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [dirty]);

  return (
    <ModalDialog
      open={pendingTo != null}
      onOpenChange={(open) => {
        if (!open) setPendingTo(null);
      }}
      title="Есть несохранённые изменения"
      dataTestId="unsaved-changes-dialog"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={() => setPendingTo(null)}>
            Остаться
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => {
              const to = pendingTo;
              setPendingTo(null);
              clearUnsavedChanges();
              if (to) navigate(to);
            }}
          >
            Уйти без сохранения
          </Button>
        </div>
      }
    >
      <p className="staff-m-0">Если уйти со страницы, изменения пропадут.</p>
    </ModalDialog>
  );
}
